import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { backofficeClient } from "@/lib/api/backoffice-client";

/** intents-api authoring endpoints via the backoffice proxy. */
const INTENTS_PATH = "/proxy/intents/api/v1/catalog-intents";

export type IntentStatus = "draft" | "active";

/** Mirrors intents-api GuidanceModel (extra=forbid): snake_case only. */
export interface IntentGuidance {
  purpose?: string;
  when_to_use?: string;
  when_not_to_use?: string[];
  guidelines?: string[];
  examples?: string[];
  deflection?: string;
  [key: string]: unknown;
}

/** Mirrors intents-api CatalogIntentResponse. */
export interface CatalogIntent {
  id: string;
  tenant_id: string;
  desk_id: string;
  status: IntentStatus;
  name: string;
  source_ref: string | null;
  description: string | null;
  guidance: IntentGuidance | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

interface IntentListResponse {
  intents: CatalogIntent[];
}

export function useIntents(tenantId: string | undefined) {
  return useQuery({
    queryKey: ["intents", tenantId],
    queryFn: async () => {
      const { data } = await backofficeClient.get<IntentListResponse>(
        INTENTS_PATH,
        { params: { tenant_id: tenantId } },
      );
      return data.intents;
    },
    enabled: Boolean(tenantId),
    // The migration writes in the background; keep the list converging
    // on its own while the tab is focused.
    refetchInterval: 30_000,
  });
}

/** service-catalog items path via the backoffice proxy. */
const CATALOG_ITEMS_PATH = "/proxy/service-catalog/api/v1/items";

/** Loose view of a catalog form field — the real type is a wide union. */
export interface SourceItemFormField {
  id?: string;
  label?: string;
  type?: string;
  required?: boolean;
  [key: string]: unknown;
}

/** Subset of service-catalog ItemResponse the source panel shows. */
export interface SourceCatalogItem {
  id: string;
  name: string;
  icon: string | null;
  description: string;
  desk_id: string;
  ticket_type: string;
  priority: string;
  is_visible: boolean;
  deleted_at: string | null;
  form_fields: SourceItemFormField[];
  updated_at: string;
}

/** Fetch the live catalog item an intent was derived from (source_ref). */
export function useSourceCatalogItem(tenantId: string, itemId: string) {
  return useQuery({
    queryKey: ["intents", "source-item", tenantId, itemId],
    queryFn: async () => {
      const { data } = await backofficeClient.get<SourceCatalogItem>(
        `${CATALOG_ITEMS_PATH}/${itemId}`,
        { params: { tenant_id: tenantId, include_deleted: true } },
      );
      return data;
    },
    enabled: Boolean(tenantId && itemId),
  });
}

interface RepublishEventsResponse {
  items: number;
  published: number;
  failed: number;
}

/**
 * Republish the tenant's catalog item events so the normalization
 * pipeline reprocesses them. Lives on intents-api (the single funnel
 * that writes intents); the proxy turns tenant_id into x-tenant-id.
 *
 * 200 = everything enqueued; 502 = partial (safe to re-run, it
 * converges); 503 = queue unwired for this environment by design.
 */
export function useRepublishItemEvents(tenantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await backofficeClient.post<RepublishEventsResponse>(
        "/proxy/intents/api/v1/internal/catalog-intents/republish-events",
        null,
        { params: { tenant_id: tenantId } },
      );
      return data;
    },
    // onSettled, not onSuccess: a 502 is a *partial* enqueue — some
    // events landed, so the list must refresh on failure too.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["intents", tenantId] });
    },
  });
}

export interface UpdateIntentInput {
  id: string;
  status: IntentStatus;
}

export function useUpdateIntent(tenantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }: UpdateIntentInput) => {
      const { data } = await backofficeClient.patch<CatalogIntent>(
        `${INTENTS_PATH}/${id}`,
        patch,
        { params: { tenant_id: tenantId } },
      );
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["intents", tenantId] });
    },
  });
}

export function useDeleteIntent(tenantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await backofficeClient.delete(`${INTENTS_PATH}/${id}`, {
        params: { tenant_id: tenantId },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["intents", tenantId] });
    },
  });
}
